import { displayError, addEventListenerIfNotExists } from "./common";
import HttpClient from "./http-client";
import LoadingIndication from "./loading-indication";
import { ensureRecaptchaToken } from "./recaptcha";

export default class Form {
    private url: string;
    private formElement: HTMLFormElement;
    private onSuccess: (response: string) => void;
    private onModelError: (response: string) => void;
    private onRedirect: () => void;

    /**
     * @param onRedirect Called when the server answers the submit with a redirect (e.g. the login form on success)
     *                   right before the navigation. onSuccess is NOT called for a redirected response: the response
     *                   body is the target page, not a success view, and most redirects mean "not done here".
     */
    constructor(
        url: string,
        form: HTMLFormElement,
        onSucess: (response: string) => void = null,
        onModelError: (response: string) => void = null,
        onRedirect: () => void = null
    ) {
        this.url = url;
        this.formElement = form;
        this.onSuccess = onSucess;
        this.onModelError = onModelError;
        this.onRedirect = onRedirect;
    }

    subscribeAndProcess(): void {
        addEventListenerIfNotExists(this.formElement, 'submit', async (event: Event) => {
            await this.process(event);
        });
    }

    async process(event: Event): Promise<void> {
        event.preventDefault();

        // A second submit (double click, Enter) while the first request runs would post the same form again. The flag
        // lives on the element because callers may create a new Form for every submit. After a redirect the page is
        // being left, so it stays set.
        if (this.formElement.dataset.submitting) {
            return;
        }
        this.formElement.dataset.submitting = "1";
        let redirected = false;
        try {
            redirected = await this.submit();
        } finally {
            if (!redirected) {
                delete this.formElement.dataset.submitting;
            }
        }
    }

    private async submit(): Promise<boolean> {
        LoadingIndication.show(this.formElement);
        await ensureRecaptchaToken(this.formElement);
        const formData = new FormData(this.formElement);

        // Current-Language-Id is added by HttpClient for every request (and only when the page
        // actually has one — the old explicit header here sent the string "undefined" otherwise).
        const response = await new HttpClient().fetch(this.url, {
            method: "POST",
            headers: {
                requestUrl: window.location.href
            },
            body: formData
        }, (e) => this.handleError(e));

        if(response.redirected) {
            // Navigating away (login success, auth / read-only redirect): no success callback — it would show the
            // success state and fire generate_lead on a page that is unloading. A caller that treats the redirect
            // as its success (login) hooks onRedirect instead.
            typeof this.onRedirect === "function" && response.ok && this.onRedirect();
            await this.handleRedirect(response);
            return true;
        }

        LoadingIndication.hide(this.formElement);

        if (response.ok) {
            typeof this.onSuccess === "function" && this.handleSuccess(response);
        }
        return false;
    }

    private handleError(e: Response|string): void {
        const response = e as Response;
        if(response?.status === 400){
            this.handleModelError(response);
        }
        else {
            displayError(response?.statusText ?? e as string);
        }
        LoadingIndication.hide(this.formElement);
    }

    private async handleModelError(response: Response): Promise<void> {
        const validatedViewComponent = await response.text();
        typeof this.onModelError === "function" &&
            this.onModelError(validatedViewComponent);
    }

    private async handleRedirect(response: Response): Promise<void> {
        const redirectUrl = await response.url;
        window.location.href = redirectUrl;
    }

    private async handleSuccess(response: Response): Promise<void> {
        const htmlContent = await response.text();
        typeof this.onSuccess === "function" &&
            this.onSuccess(htmlContent);
    }
}
